import { ComponentType, useRef, useState, useEffect, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import { SvgIconProps } from '@mui/material/SvgIcon';
import ButtonBase from '@mui/material/ButtonBase';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { getColors, typography } from '../tokens';

export interface TabNavItem {
  id: string;
  label: string;
  icon?: ComponentType<SvgIconProps>;
}

export interface TabNavProps {
  tabs: TabNavItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      position: 'relative',
    },
    root: {
      display: 'flex',
      gap: 24,
      overflow: 'hidden',
      flex: 1,
    },
    tab: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      fontWeight: 500,
      padding: '8px 0',
      border: 'none',
      borderBottom: '2px solid transparent',
      background: 'transparent',
      color: c.text.secondary,
      cursor: 'pointer',
      transition: 'color 0.15s ease, border-color 0.15s ease',
      whiteSpace: 'nowrap',
      maxWidth: 150,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flexShrink: 0,
      '&:hover': {
        color: c.text.primary,
      },
    },
    activeTab: {
      color: c.primary.main,
      borderBottomColor: c.primary.main,
    },
    navButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      border: 'none',
      borderRadius: '50%',
      background: fgAlpha(0.05),
      color: c.text.secondary,
      cursor: 'pointer',
      flexShrink: 0,
      transition: 'background 0.15s ease, color 0.15s ease',
      '& svg': { width: 20, height: 20 },
      '&:hover': {
        background: fgAlpha(0.1),
        color: c.text.primary,
      },
    },
  };
});

export const TabNav = ({
  tabs,
  activeTab,
  onChange,
}: Readonly<TabNavProps>) => {
  const { classes } = useStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkOverflow = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow, tabs]);

  const scroll = (direction: 'left' | 'right') => {
    const el = containerRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({
      left: direction === 'right' ? amount : -amount,
      behavior: 'smooth',
    });
    setTimeout(checkOverflow, 300);
  };

  return (
    <div className={classes.wrapper}>
      {canScrollLeft && (
        <ButtonBase
          className={classes.navButton}
          onClick={() => scroll('left')}
          aria-label="Previous tabs"
        >
          <ChevronLeftIcon />
        </ButtonBase>
      )}
      <div className={classes.root} ref={containerRef} onScroll={checkOverflow}>
        {tabs.map(tab => {
          const isActive = tab.id === activeTab;
          return (
            <ButtonBase
              key={tab.id}
              className={`${classes.tab} ${isActive ? classes.activeTab : ''}`}
              onClick={() => onChange(tab.id)}
            >
              {tab.label}
            </ButtonBase>
          );
        })}
      </div>
      {canScrollRight && (
        <ButtonBase
          className={classes.navButton}
          onClick={() => scroll('right')}
          aria-label="More tabs"
        >
          <ChevronRightIcon />
        </ButtonBase>
      )}
    </div>
  );
};
